/**
 * Preset rule policies - the dropdown options in Settings → Plugin → Rules.
 * Admins pick a preset to seed, then tweak in the rule editor.
 */

import type { Rule } from './types';

export interface Preset {
  id: string;
  name: string;
  description: string;
  rules: Rule[];
}

export const RULE_PRESETS: Preset[] = [
  {
    id: 'manual_only',
    name: 'Manual only (safest, cheapest)',
    description:
      'Nothing fires automatically. Reader clicks "Send for triage" per study. Zero waste; defeats the pre-read value for AI triage plugins.',
    rules: [],
  },
  {
    id: 'on_worklist_view',
    name: 'On worklist view (recommended default)',
    description:
      'Fires when the study appears in a reader\'s active worklist filter. Pays only for studies actually being reviewed. Debounced + cached.',
    rules: [
      {
        priority: 10,
        enabled: true,
        name: 'Eligible CXR on worklist view',
        when: 'on_worklist_view',
        match: {
          modality: ['CR', 'DX'],
          bodyPartContains: ['CHEST', 'THORAX'],
          ageMin: 18,
        },
        action: 'send',
      },
    ],
  },
  {
    id: 'daytime_auto_arrive',
    name: 'Daytime auto-arrive',
    description:
      'Fires immediately on arrival for daytime studies (08:00-20:00). Off-hours fall through to manual.',
    rules: [
      {
        priority: 10,
        enabled: true,
        name: 'Daytime eligible CXR',
        when: 'on_study_arrive',
        match: {
          modality: ['CR', 'DX'],
          bodyPartContains: ['CHEST', 'THORAX'],
          ageMin: 18,
          timeWindow: '08:00-20:00',
        },
        action: 'send',
      },
    ],
  },
  {
    id: 'twentyfour_seven_auto',
    name: '24/7 auto-arrive (maximum coverage, maximum spend)',
    description:
      'Every eligible CXR fires on arrival. Highest cost; includes re-pushes and cancelled appointments.',
    rules: [
      {
        priority: 10,
        enabled: true,
        name: 'All eligible CXR on arrival',
        when: 'on_study_arrive',
        match: {
          modality: ['CR', 'DX'],
          bodyPartContains: ['CHEST', 'THORAX'],
          ageMin: 18,
        },
        action: 'send',
      },
    ],
  },
  {
    id: 'er_priority',
    name: 'ER priority only',
    description:
      'Only fires on configured ER modality AE titles. Everything else is manual. Minimum spend; catches true emergencies before readers open the worklist.',
    rules: [
      {
        priority: 10,
        enabled: true,
        name: 'ER CXR on arrival',
        when: 'on_study_arrive',
        match: {
          modality: ['CR', 'DX'],
          bodyPartContains: ['CHEST', 'THORAX'],
          ageMin: 18,
          sendingAe: ['ER_AE', 'ER_MODALITY'],  // admin edits to match their site
        },
        action: 'send',
      },
    ],
  },
];

/** Standard "always skip" exclusion rules admins commonly add on top of
 *  a preset to keep LIRA inside its validated scope. */
export const COMMON_SKIP_RULES: Rule[] = [
  {
    priority: 1,
    enabled: true,
    name: 'Skip pediatric (outside LIRA validated scope)',
    when: 'on_study_arrive',
    match: { ageMax: 17 },
    action: 'skip',
  },
  {
    priority: 2,
    enabled: true,
    name: 'Skip non-CR/DX modalities',
    when: 'on_study_arrive',
    match: {},  // Modality mismatch handled by inverting — admin must edit
    action: 'skip',
  },
];
